import { useRef, useEffect, useMemo } from 'react';

/**
 * A custom React hook that returns a debounced version of the provided callback function.
 * The debounced function will delay invoking the callback until after the specified delay
 * has elapsed since the last time it was called. This is useful for optimizing performance
 * in scenarios like search inputs, window resize handlers, or API calls that shouldn't
 * fire too frequently.
 *
 * @template T - A function type that extends `(...args: any[]) => void`
 * @param {T} callback - The function to debounce. The callback reference is kept stable
 * internally, so it's safe to pass an inline function.
 * @param {number} delay - The number of milliseconds to delay the callback execution.
 *
 * @returns A debounced version of the callback with the same
 * signature, plus a `cancel` method that can be called to cancel any pending execution.
 *
 * @example
 * ```tsx
 * const handleSearch = useDebouncedCallback((searchTerm: string) => {
 *   fetchResults(searchTerm);
 * }, 500);
 *
 * // Use in an input handler
 * <input onChange={(e) => handleSearch(e.target.value)} />
 *
 * // Cancel pending execution if needed
 * handleSearch.cancel();
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- This is a generic function that can be used with any function
export function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
) {
  const timeoutRef = useRef<number | null>(null);
  const stableCallbackRef = useRef(callback);

  useEffect(() => {
    stableCallbackRef.current = callback;
  }, [callback]);

  const debounced = useMemo(() => {
    const fn = (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        stableCallbackRef.current(...args);
      }, delay);
    };

    fn.cancel = () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    return fn as T & { cancel: () => void };
  }, [delay]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debounced;
}
